/**
 * Achievement Utility Functions
 * Helpers for mapping criteria types to categories and icons
 */

import { SystemAchievementCriteriaType } from '@api/types/achievements'

// ===================
// CATEGORY MAPPING
// ===================

/**
 * Map criteria type to human-readable category name
 */
export function getCriteriaCategory(criteriaType: SystemAchievementCriteriaType | null): string {
  if (criteriaType === null) return 'Collections'

  switch (criteriaType) {
    // Adventure completions (1-5)
    case SystemAchievementCriteriaType.TotalAdventuresCompleted:
    case SystemAchievementCriteriaType.TotalAdventuresFailed:
    case SystemAchievementCriteriaType.TotalSoloAdventuresCompleted:
    case SystemAchievementCriteriaType.TotalGroupAdventuresCompleted:
    case SystemAchievementCriteriaType.UniqueAdventuresCompleted:
      return 'Exploration'

    // Category adventures (6)
    case SystemAchievementCriteriaType.CategoryAdventuresCompleted:
      return 'Exploration'

    // Locations, geocaches (10-11)
    case SystemAchievementCriteriaType.TotalLocationsVisited:
    case SystemAchievementCriteriaType.TotalGeoCachesFound:
      return 'Discovery'

    // Events attended (20)
    case SystemAchievementCriteriaType.TotalEventsAttended:
      return 'Events'

    // Friends, groups, messages (30-32)
    case SystemAchievementCriteriaType.TotalFriendsMade:
    case SystemAchievementCriteriaType.TotalGroupSessionsLed:
    case SystemAchievementCriteriaType.TotalMessagesSent:
      return 'Social'

    // Streaks (40-41)
    case SystemAchievementCriteriaType.ConsecutiveDaysActive:
    case SystemAchievementCriteriaType.WeeklyCompletionStreak:
      return 'Dedication'

    // Speed, perfect runs (50-52)
    case SystemAchievementCriteriaType.TopAdventureCompletion:
    case SystemAchievementCriteriaType.PerfectAdventureCompletion:
    case SystemAchievementCriteriaType.SpeedCompletion:
      return 'Performance'

    // Custom (100)
    case SystemAchievementCriteriaType.Custom:
      return 'Special'

    default:
      return 'Other'
  }
}

// ===================
// ICON MAPPING
// ===================

/** Icon name for MUI icons based on criteria type */
export type AchievementIconName =
  | 'Hiking'
  | 'Explore'
  | 'LocationOn'
  | 'Event'
  | 'Groups'
  | 'LocalFireDepartment'
  | 'Timer'
  | 'Star'
  | 'EmojiEvents'
  | 'Public'

/**
 * Get icon name for a criteria type
 */
export function getCriteriaIconName(criteriaType: SystemAchievementCriteriaType | null): AchievementIconName {
  if (criteriaType === null) return 'EmojiEvents'

  switch (criteriaType) {
    // Adventure completions
    case SystemAchievementCriteriaType.TotalAdventuresCompleted:
    case SystemAchievementCriteriaType.TotalAdventuresFailed:
    case SystemAchievementCriteriaType.TotalSoloAdventuresCompleted:
    case SystemAchievementCriteriaType.UniqueAdventuresCompleted:
      return 'Hiking'

    case SystemAchievementCriteriaType.TotalGroupAdventuresCompleted:
    case SystemAchievementCriteriaType.CategoryAdventuresCompleted:
      return 'Explore'

    // Locations, geocaches
    case SystemAchievementCriteriaType.TotalLocationsVisited:
    case SystemAchievementCriteriaType.TotalGeoCachesFound:
      return 'LocationOn'

    // Events
    case SystemAchievementCriteriaType.TotalEventsAttended:
      return 'Event'

    // Social
    case SystemAchievementCriteriaType.TotalFriendsMade:
    case SystemAchievementCriteriaType.TotalGroupSessionsLed:
    case SystemAchievementCriteriaType.TotalMessagesSent:
      return 'Groups'

    // Streaks
    case SystemAchievementCriteriaType.ConsecutiveDaysActive:
    case SystemAchievementCriteriaType.WeeklyCompletionStreak:
      return 'LocalFireDepartment'

    // Performance
    case SystemAchievementCriteriaType.SpeedCompletion:
      return 'Timer'

    case SystemAchievementCriteriaType.TopAdventureCompletion:
    case SystemAchievementCriteriaType.PerfectAdventureCompletion:
      return 'Star'

    // Custom/Special
    case SystemAchievementCriteriaType.Custom:
      return 'EmojiEvents'

    default:
      return 'Public'
  }
}

/**
 * Get icon color for a criteria type
 */
export function getCriteriaIconColor(criteriaType: SystemAchievementCriteriaType | null): string {
  if (criteriaType === null) return '#ffc107' // Collection - gold

  switch (criteriaType) {
    // Exploration - green
    case SystemAchievementCriteriaType.TotalAdventuresCompleted:
    case SystemAchievementCriteriaType.TotalAdventuresFailed:
    case SystemAchievementCriteriaType.TotalSoloAdventuresCompleted:
    case SystemAchievementCriteriaType.TotalGroupAdventuresCompleted:
    case SystemAchievementCriteriaType.UniqueAdventuresCompleted:
    case SystemAchievementCriteriaType.CategoryAdventuresCompleted:
      return '#4caf50'

    // Discovery - cyan
    case SystemAchievementCriteriaType.TotalLocationsVisited:
    case SystemAchievementCriteriaType.TotalGeoCachesFound:
      return '#00bcd4'

    // Events - orange
    case SystemAchievementCriteriaType.TotalEventsAttended:
      return '#ff9800'

    // Social - purple
    case SystemAchievementCriteriaType.TotalFriendsMade:
    case SystemAchievementCriteriaType.TotalGroupSessionsLed:
    case SystemAchievementCriteriaType.TotalMessagesSent:
      return '#9c27b0'

    // Dedication - deep orange (fire)
    case SystemAchievementCriteriaType.ConsecutiveDaysActive:
    case SystemAchievementCriteriaType.WeeklyCompletionStreak:
      return '#ff5722'

    // Performance - red/amber
    case SystemAchievementCriteriaType.TopAdventureCompletion:
    case SystemAchievementCriteriaType.PerfectAdventureCompletion:
      return '#ffc107'
    case SystemAchievementCriteriaType.SpeedCompletion:
      return '#f44336'

    // Custom - blue
    case SystemAchievementCriteriaType.Custom:
      return '#2196f3'

    default:
      return '#607d8b'
  }
}

// ===================
// STREAK HELPERS
// ===================

/**
 * Check if criteria type is a streak-based achievement
 */
export function isStreakAchievement(criteriaType: SystemAchievementCriteriaType | null): boolean {
  if (criteriaType === null) return false
  return (
    criteriaType === SystemAchievementCriteriaType.ConsecutiveDaysActive ||
    criteriaType === SystemAchievementCriteriaType.WeeklyCompletionStreak
  )
}

/**
 * Get streak unit label (e.g., "days", "weeks")
 */
export function getStreakUnitLabel(criteriaType: SystemAchievementCriteriaType, count: number): string {
  if (criteriaType === SystemAchievementCriteriaType.WeeklyCompletionStreak) {
    return count === 1 ? 'week' : 'weeks'
  }
  return count === 1 ? 'day' : 'days'
}

// ===================
// PROGRESS HELPERS
// ===================

/**
 * Format progress display (e.g., "3 / 10")
 */
export function formatProgress(currentValue: number, threshold: number): string {
  return `${currentValue} / ${threshold}`
}

/**
 * Calculate remaining count to complete
 */
export function getRemainingCount(currentValue: number, threshold: number): number {
  return Math.max(0, threshold - currentValue)
}
